import { makeAutoObservable } from 'mobx'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { MergeField } from 'entities/MergeField/model/MergeField.ts'
import { type ICallingActionPayload } from 'entities/AICalling/api/types.ts'
import { Attachment } from 'entities/Attachment/model/Attachment.ts'
import {
  EmojiAction,
  EnumVariantMessageField,
  makeSavedReplyInputAction,
  mergeFieldReplacer,
  MessageFieldStore,
  SavedRepliesAction,
  ShortenLinkAction,
} from 'widgets/MessageField'
import { MediaAction } from 'widgets/MessageField/ui/FieldActions/MediaAction/MediaAction.tsx'
import { MergeFieldsIconAction } from 'widgets/MergeField/ui/actions/MergeFieldsIconAction/MergeFieldsIconAction.tsx'
import { SavedRepliesStore } from 'widgets/SavedReplies'

export class AgentSMSMessageStore {
  readonly messageFieldStore
  private readonly _savedRepliesStore
  constructor(
    readonly entities: IEntitiesStore,
    data?: Pick<ICallingActionPayload['settings'], 'message' | 'media_url'>
  ) {
    this._savedRepliesStore = new SavedRepliesStore(this.entities)
    this.messageFieldStore = new MessageFieldStore(
      {
        makeEvents: () => ({
          onFocus: () => {
            this._error = ''
          },
        }),
        placeholder: 'Write your message...',
        styles: {
          minHeight: 102,
          maxHeight: 200,
        },
        variant: EnumVariantMessageField.Default,
        showActionsItems: 5,
        resetOnUnmount: false,
        makeInputActions: (store) => [makeSavedReplyInputAction(store, this._savedRepliesStore)],
        makeActions: () => [
          { iconButtonComponent: <MediaAction /> },
          { iconButtonComponent: <SavedRepliesAction store={this._savedRepliesStore} useHotKey /> },
          { iconButtonComponent: <EmojiAction /> },
          {
            iconButtonComponent: (
              <MergeFieldsIconAction
                onAddMergeField={(value) => this._onAddMergeField(value)}
                isAllIntegration
              />
            ),
          },
          { iconButtonComponent: <ShortenLinkAction /> },
        ],
      },
      this.entities
    )

    if (data) {
      const attachments =
        data.media_url?.map(
          (item) => new Attachment({ requestAttachment: item, fromMediaUrl: true })
        ) ?? []
      this.messageFieldStore.setMessageData({
        message: data.message,
        attachments,
        isFocus: false,
        isReset: false,
        isReplace: true,
      })
    }

    makeAutoObservable(this)
  }

  get params() {
    return {
      message: this.messageFieldStore.text,
      media_url: this.messageFieldStore.media_url,
    }
  }
  private _onAddMergeField = (field: MergeField) => {
    if (!this.messageFieldStore.addContent) return
    const fieldContent = mergeFieldReplacer(this.entities, field.template) + '&nbsp;'
    this.messageFieldStore.addContent(fieldContent)
  }

  get isEmpty() {
    return !this.messageFieldStore.text.trim() && this.messageFieldStore.attachments.length === 0
  }
  get loadingAttachment() {
    return this.messageFieldStore.loadingAttachment
  }

  clear = () => {
    this.messageFieldStore.clear()
  }
  reset = () => {
    this.messageFieldStore.reset()
  }
  setMessageData: typeof this.messageFieldStore.setMessageData = (props) => {
    this.messageFieldStore.setMessageData(props)
  }

  private _error = ''
  get error() {
    return this._error
  }
  validate = () => {
    let isValid = true
    if (this.isEmpty) {
      isValid = false
      this._error = 'Add SMS message'
    }
    return isValid
  }

  get disabled() {
    return this.loadingAttachment
  }
}
